/**
 * הנתונים של "תמונת מצב" — נבנים בצד הלקוח מהנקודות שכבר קיימות בשרת.
 *
 * הבחירה המכוונת: אין כאן אף endpoint חדש. הקריאות שהמערכת כבר יודעת להגיש —
 * תנועות, בקשות תמיכה, הוראות קבע, נתמכים, ותרומות ממתינות לקרן שדגל התרומות
 * דלוק אצלה — יחד עם היתרה שיושבת ב-FundContext, וכל החישוב נעשה
 * כאן. זה מה שמאפשר להעלות את המסך לאוויר בלי לגעת בשרת. בהטמעה המלאה החישוב
 * עובר ל-endpoint אחד מרוכז עם קאשינג.
 *
 * החישוב עצמו יושב ב-buildDashboardModel, פונקציה טהורה שמקבלת את מה שהשרת
 * החזיר ומחזירה את מה שהמסך מציג — כדי שאפשר יהיה לבדוק אותו בלי שרת ובלי React.
 *
 * טווח: חלון מתגלגל (7 / 30 / 90 / 365 ימים אחורה מהיום), או טווח מותאם מהבורר.
 * התנועות נמשכות לחלון כפול — התקופה הנוכחית + הקודמת באותו אורך — כדי שהשוואת
 * המגמה תעלה מאותה קריאה. בטווח מותאם ארוך אין תקופה קודמת (ר' resolveRanges).
 */

import { useMemo } from 'react';
import useFundContext from '@/contexts/Fund/useFundContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundAccess from '@/hooks/useFundAccess';
import type { RefreshableQueryMeta } from '@/hooks/useGlobalRefresh';
import type Balance from '@/models/Balance';
import Transaction from '@/models/Transaction';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import SupportedDtoIn from '@/models/in/SupportedDtoIn';
import { getStatusGroup } from '@/helpers/requestStatus';
import { ExpirationStatus, getExpirationStatus, EXPIRATION_SOON_DAYS } from '@/helpers/contactClassificationExpiration';
import type { PendingOfflineDonation } from '@/pages/Donation/DonationsAdmin/types';
// אותו כלל "פעילה" ואותו זיהוי מטבע שבמסך הוראות הקבע, ולא העתק שלהם — כדי ששני המסכים לא ייפרדו.
import { getCurrencyIso, isCollecting, sumByCurrency } from '@/pages/Donation/StandingOrders/standingOrderModel';
import { CHART, currencySymbol } from './chartTheme';
import type { ChartRow, RankItem } from './charts';

export type PeriodKey = 'week' | 'month' | 'quarter' | 'year' | 'custom';
export type Granularity = 'day' | 'week' | 'month' | 'year';

/** טווח מותאם, כפי שהוא יוצא מבוררי התאריכים (yyyy-mm-dd). */
export interface CustomRange {
  from: string;
  to: string;
}

export const PERIOD_LABELS: Record<PeriodKey, string> = {
  week: 'שבוע',
  month: 'חודש',
  quarter: 'רבעון',
  year: 'שנה',
  custom: 'מותאם',
};

export const PERIOD_COMPARE: Record<PeriodKey, string> = {
  week: 'מהשבוע שעבר',
  month: 'מהחודש שעבר',
  quarter: 'מהרבעון הקודם',
  year: 'מהשנה שעברה',
  custom: 'מהתקופה הקודמת באותו אורך',
};

const PERIOD_DAYS: Record<Exclude<PeriodKey, 'custom'>, number> = { week: 7, month: 30, quarter: 90, year: 365 };

/** בקשה שיושבת באישור יותר מזה — הרכז צריך לדעת עליה. */
export const STALE_REQUEST_DAYS = 7;

/** טווח ארוך מזה (בערך שנה וחצי) עובר לתצוגה שנתית בכרטיס "תרומות לפי". */
export const YEAR_VIEW_THRESHOLD_DAYS = 540;

/**
 * טווח ארוך מזה מוצג בחודשים ולא בימים: בגרף התזרים, בספארקליינים ובתורמים
 * החדשים. בטווח של שנים, עמודה ליום היא אלפי עמודות שאי אפשר לקרוא.
 */
export const MONTH_VIEW_THRESHOLD_DAYS = 120;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * החלון של "ממתינות לאישור ראשוני": כל הבקשות, ולא התקופה שנבחרה בבורר.
 *
 * הכרטיס אומר מה תקוע עכשיו, וזו אינה שאלה של תקופה: בקשה שממתינה לאישור
 * מאז מרץ היא הדחופה ביותר, ודווקא היא נעלמה כשהבורר עמד על "חודש". מ-2015
 * כמו במסך הבקשות עצמו, ועד סוף החודש הנוכחי כדי לכלול בקשות שהוגשו לחודש
 * הבא.
 */
const attentionRange = (): Range => ({
  from: new Date(2015, 0, 1),
  to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
});

const HE_MONTHS = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];
const HE_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/* ============================ עזרי תאריך ============================ */

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const iso = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dm = (d: Date): string => `${d.getDate()}.${d.getMonth() + 1}`;

/** yyyy-mm-dd כתאריך מקומי. `new Date('2026-01-01')` נקרא כ-UTC, ומערבית ל-UTC הוא נופל יום אחורה. */
const parseLocalDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

export interface Range {
  from: Date;
  to: Date;
}

/** BalanceRangeDto מהשרת: היתרה לפני היום הראשון בטווח, והיתרה בסוף האחרון (או היום). */
export interface BalanceRange {
  start: string;
  end: string;
  openingBalance: number;
  closingBalance: number;
}

/** מספר הימים בטווח, כולל שני הקצוות. */
export const daysInRange = (range: Range): number =>
  Math.max(1, Math.round((startOfDay(range.to).getTime() - startOfDay(range.from).getTime()) / DAY_MS) + 1);

/**
 * החלון הנוכחי והחלון הקודם באותו אורך. בטווח מותאם התאריכים באים מהבורר;
 * טווח הפוך או לא תקין נופל בחזרה לחודש, כדי שהמסך לא ייראה ריק בלי סיבה.
 *
 * בטווח מותאם ארוך מהתצוגה השנתית אין חלון קודם: הבורר נפתח על 2020 עד היום,
 * ו"התקופה הקודמת באותו אורך" הייתה עוד שש שנים אחורה — קריאה כפולה מול השרת
 * בשביל אחוז שינוי שאיש לא קורא. המדדים מציגים במקומו מקף.
 */
export const resolveRanges = (period: PeriodKey, custom?: CustomRange, today: Date = new Date()): { current: Range; previous: Range | null } => {
  const day = startOfDay(today);
  let current: Range;

  if (period === 'custom') {
    const from = custom?.from ? parseLocalDate(custom.from) : null;
    const to = custom?.to ? parseLocalDate(custom.to) : null;
    current = from && to && from <= to ? { from, to } : { from: addDays(day, -(PERIOD_DAYS.month - 1)), to: day };
  } else {
    current = { from: addDays(day, -(PERIOD_DAYS[period] - 1)), to: day };
  }

  const days = daysInRange(current);
  if (period === 'custom' && days > YEAR_VIEW_THRESHOLD_DAYS) return { current, previous: null };
  return { current, previous: { from: addDays(current.from, -days), to: addDays(current.from, -1) } };
};

/**
 * הרזולוציה שבה גרף התזרים מוצג בפועל. הבחירה של הרכז נשמרת, אבל בטווח ארוך
 * עמודה ליום (או לשבוע) היא אלפי עמודות - ואז הגרף עולה לחודש.
 */
export const effectiveFlowGranularity = (wanted: Granularity, periodDays: number): Granularity => {
  if (periodDays > YEAR_VIEW_THRESHOLD_DAYS) return 'month';
  if (periodDays > MONTH_VIEW_THRESHOLD_DAYS && wanted === 'day') return 'week';
  return wanted;
};

/** תוויות הדליים של ציר הזמן, מהישן לחדש (הישן יושב מימין ב-RTL). */
interface Bucket {
  from: Date;
  to: Date;
  label: string;
  full: string;
}

const buildBuckets = (range: Range, gran: Granularity): Bucket[] => {
  const out: Bucket[] = [];
  if (gran === 'day') {
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) {
      out.push({
        from: d,
        to: d,
        label: dm(d),
        full: `${d.getDate()} ב${HE_MONTHS[d.getMonth()].replace('׳', '')}׳`,
      });
    }
    return out;
  }
  if (gran === 'week') {
    for (let d = range.from; d <= range.to; d = addDays(d, 7)) {
      const end = addDays(d, 6) > range.to ? range.to : addDays(d, 6);
      out.push({ from: d, to: end, label: dm(d), full: `${dm(d)} – ${dm(end)}` });
    }
    return out;
  }
  if (gran === 'year') {
    for (let y = range.from.getFullYear(); y <= range.to.getFullYear(); y++) {
      const from = new Date(y, 0, 1) < range.from ? range.from : new Date(y, 0, 1);
      const to = new Date(y, 11, 31) > range.to ? range.to : new Date(y, 11, 31);
      const partial = from.getMonth() !== 0 || from.getDate() !== 1 || to.getMonth() !== 11 || to.getDate() !== 31;
      out.push({ from, to, label: partial ? `${y} (חלקית)` : `${y}`, full: `${y}` });
    }
    return out;
  }
  let cursor = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
  while (cursor <= range.to) {
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    out.push({
      from: cursor < range.from ? range.from : cursor,
      to: end > range.to ? range.to : end,
      label: HE_MONTHS[cursor.getMonth()],
      full: `${HE_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`,
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return out;
};

/* ============================ סיווג תנועות ============================ */

const DONATION_KIND = 'תרומה';

const txDate = (t: Transaction): Date => startOfDay(new Date(t.date));

/**
 * הסכום במטבע הדיווח של הקרן, או null כשאין המרה.
 *
 * השרת מחזיר את סכום התנועה ב-`amount` במטבע התנועה, ואת שוויו השקלי ב-`ilsAmount` —
 * שדה שיכול להיות ריק. לכן תנועה במטבע שאינו מטבע הקרן ניתנת להמרה רק כשהקרן מדווחת
 * בשקלים ויש שער; אחרת היא נספרת כ"לא ניתנת להמרה" ומדווחת, ולא נכנסת לסכום כאילו
 * המספר הדולרי הוא שקלים.
 */
const reportingAmount = (t: Transaction, fundCurrency: string): number | null => {
  const txCurrency = t.currency || fundCurrency;
  if (txCurrency === fundCurrency) return t.amount ?? 0;
  if (fundCurrency === 'ILS') return t.ilsAmount ?? null;
  return null;
};

/**
 * נכנס מול יוצא נקבע לפי הסימן של הסכום ולא לפי מילת הסוג: השרת מחזיר תרומות,
 * הלוואות ופיקדונות בסימן שלהן ומנגד את כל השאר (TransactionsService), בעוד ש-`kind`
 * הוא טקסט חופשי שכולל גם "לא ידוע" — שינוי מילה בשרת היה הופך תרומות ליציאה בשקט.
 *
 * הסימן נושא גם החזרים: תרומה שהוחזרה לתורם מגיעה כ"תרומה" בסכום שלילי, ותמיכה
 * שחזרה מהבנק מגיעה כ"תמיכה" בסכום חיובי. לכן סכומי התרומות והתמיכות הם נטו
 * (החזר מקטין אותם), ורשימות "תרומה אחת = שורה" לוקחות רק את התשלומים עצמם.
 */
const isDonation = (t: Transaction): boolean => t.kind === DONATION_KIND;

/* "תמיכה" ו"מלגה" הם אותו סוג מסמך בהנהלת החשבונות (מלגות), והם נבדלים רק
 * באמצעי התשלום: בהוראת קבע השרת קורא לזה "תמיכה", ואחרת "מלגה". שניהם כסף
 * שיצא לנתמכים, ולכן המדד מחבר את שניהם ואומר זאת. */
const isPayout = (t: Transaction): boolean => t.kind === 'תמיכה' || t.kind.includes('מלגה');

/**
 * אמצעי התשלום של התרומה — כפי שהשרת מחזיר אותו, בלי שכבת פרשנות.
 *
 * קדמה לכאן חלוקה ל"ערוצים" שבה "נדרים פלוס" זוהה כתרומה באשראי או בביט שיש
 * לה donationReference. ההנחה הזו לא עמדה בבדיקה: donationReference הוא שדה
 * ה"אסמכתא" הכללי של התנועה ב-APT, ואילו קודי התשלום הייעודיים של נדרים כלל
 * אינם ממופים ב-StandardizePaymentMethod ומגיעים ללקוח כ-"other".
 *
 * לכן הפילוח הוא לפי אמצעי התשלום עצמו: מפתחות שהשרת מייצר, ותוויות מקבצי
 * התרגום — אותן שמות שמסך התנועות מראה.
 */
const PAYMENT_COLORS = [CHART.wine, CHART.steelMid, CHART.teal, CHART.brass, CHART.plum, CHART.steelSoft, CHART.tealSoft, CHART.wineLift];

/** פרוסה בפילוח אמצעי התשלום. המפתח מתורגם בתצוגה. */
export interface PaymentSlice {
  key: string;
  value: number;
  color: string;
}

const median = (values: number[]): number => {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/** טווחי סכום להתפלגות הוראות הקבע. התוויות נבנות עם סימן המטבע של הקרן. */
const soBuckets = (s: string) => [
  { label: `≤ ${s}100`, max: 100 },
  { label: `${s}100–250`, max: 250 },
  { label: `${s}250–500`, max: 500 },
  { label: `${s}500–1K`, max: 1000 },
  { label: `${s}1K +`, max: Infinity },
];

const amountBuckets = (s: string) => [
  { label: `≤ ${s}100`, max: 100 },
  { label: `${s}100–360`, max: 360 },
  { label: `${s}360–1K`, max: 1000 },
  { label: `${s}1K–3K`, max: 3000 },
  { label: `${s}3K–10K`, max: 10000 },
  { label: `${s}10K +`, max: Infinity },
];

/* ============================ בקשות תמיכה ============================ */

/*
 * שלמה: "דורש טיפול" ערבב שני סוגי דברים שונים - נתמך שהסיווג שלו עומד לפוג
 * (הרכז יכול לפעול: לחדש) מול בקשת תמיכה שממתינה לאישור ראשוני במשרד (הרכז
 * לא יכול לקדם את זה, רק לחכות). ההכרעה: "דורש טיפול" מצטמצם לנתמכים שהסיווג
 * שלהם עומד לפוג בלבד. בקשות ממתינות לאישור ראשוני מוצגות בנפרד, כמדד
 * אינפורמטיבי בלי צביעת אזעקה - ראו PendingInitialApprovalInfo למטה.
 */
export type AttentionId = 'expiringClassifications';

export interface AttentionItem {
  id: AttentionId;
  severity: 'critical' | 'warning';
  title: string;
  detail: string;
  count: number;
  /** הנתיב שממנו מטפלים בזה, יחסית לקרן. */
  to: string;
}

/**
 * בקשות תמיכה שממתינות לאישור ראשוני מעל STALE_REQUEST_DAYS ימים - מוצג בנפרד
 * מ"דורש טיפול" ובלי צביעת אזעקה, כי זה תקוע אצל המשרד ולא אצל הרכז.
 */
export interface PendingInitialApprovalInfo {
  count: number;
  /** התאריך הכי ותיק שבין הבקשות הממתינות, אם יש. */
  oldestAt: number | null;
  to: string;
}

/**
 * סטטוסים של חודש שאינו מאושר: עוד לא אושר (ממתין למשרד, לחתימות או לוועדת
 * חריגים), או שנסגר בלי תשלום (נדחה, בוטל, התשלום חזר).
 */
const NOT_APPROVED_MONTH_STATUSES = new Set([
  'PendingSignatures',
  'PendingInitialApproval',
  'PendingExceptionsCommitteeApproval',
  'Rejected',
  'Cancelled',
  'PaymentReturned',
]);

/**
 * הסכום שאושר בפועל לחודשים שבתקופה.
 *
 * `approvedAmountTotal` אינו כזה: השרת ממלא את הסכום המאושר בסכום המבוקש כבר
 * בהגשה ולא מאפס אותו בדחייה או בביטול, ולכן בקשה שנדחתה, בוטלה או עדיין ממתינה
 * מגיעה עם "סכום מאושר" מלא. במקומו נסכמים חודש-חודש: סכום החודש ב-`approvedAmounts`,
 * הסטטוס שלו ב-`statuses` והתאריך ב-`requestedForDates` — שלושתם מקבילים מרגע
 * שהבקשה עברה אישור ראשוני (SupportsService). לפני כן אין חודש מאושר אחד.
 *
 * מול שרת ישן שאינו מחזיר `approvedAmounts` נופלים ל-`approvedAmountTotal` רק
 * כשהבקשה כבר אושרה ראשונית.
 */
export const approvedInRange = (r: SupportRequestDtoIn, range: Range): number => {
  if (r.isInInitialStatusProcess) return 0;
  const statuses = r.statuses ?? [];
  const dates = r.requestedForDates ?? [];
  const amounts = r.approvedAmounts;
  if (!amounts || amounts.length !== dates.length || statuses.length !== dates.length) return r.approvedAmountTotal ?? 0;

  let total = 0;
  dates.forEach((value, index) => {
    const date = startOfDay(new Date(value));
    if (date < range.from || date > range.to) return;
    if (NOT_APPROVED_MONTH_STATUSES.has(statuses[index])) return;
    total += amounts[index] ?? 0;
  });
  return total;
};

/**
 * ממתינה לאישור הראשוני עצמו - לא רק שאף חודש לא אושר.
 *
 * בשרת (SupportsService), isInInitialStatusProcess הוא true על כל סטטוס
 * ראשוני שאינו Approved - וזה כולל גם Rejected, Cancelled, PendingCompletion
 * ו-CompletionReturned. בקשה שנדחתה או בוטלה אינה "תקועה", היא סגורה; ובקשה
 * שממתינה להשלמת מסמכים היא קטגוריה שונה. "ממתינה לאישור" היא רק מי שממתינה
 * לבדיקה או לחתימות, ולכן הבדיקה כאן היא על קבוצת הסטטוס עצמה ולא רק על השדה
 * הבוליאני. אותו תנאי בדיוק יושב ב-initialOnly של מסך הבקשות.
 */
const isPendingInitialReview = (status: string): boolean => {
  const group = getStatusGroup(status)?.id;
  return group === 'PendingApproval' || group === 'PendingSignatures';
};

/**
 * הקישור למסך הבקשות, מסונן בדיוק למה שנספר כאן: אותו חלון תאריכים, אותו
 * משך המתנה (ימים מאז ההגשה), ו"אישור ראשוני בלבד".
 */
const requestsLink = (fundId: number, range: Range, waitingOver: number): string =>
  `/${fundId}/support/support-requests?dateFrom=${iso(range.from)}&dateTo=${iso(range.to)}&waitingOver=${waitingOver}&initialOnly=1`;

/**
 * מה ממתין עכשיו.
 *
 * "דורש טיפול" = נתמכים שיש להם סיווג שעומד לפוג, באותו כלל בדיוק (getExpirationStatus)
 * שבו מסך הנתמכים מסנן את "עומד לפוג" — והקישור פותח את המסך על הסינון הזה, כך
 * שהמספר שנלחץ הוא מספר השורות שנפתחות. סיווג שפג תוקפו חוסם בקשת תמיכה חדשה,
 * ולכן הוא כאן ולא במסך הנתמכים בלבד.
 */
const buildAttention = (
  requests: SupportRequestDtoIn[],
  supporteds: SupportedDtoIn[],
  fundId: number,
  range: Range,
  now: number
): { attention: AttentionItem[]; pendingInitialApproval: PendingInitialApprovalInfo | null } => {
  const staleThreshold = now - STALE_REQUEST_DAYS * DAY_MS;

  let staleCount = 0;
  let oldestStale: number | null = null;

  requests.forEach((request) => {
    const requestedAt = new Date(request.requestedAt).getTime();
    const statuses = request.statuses ?? [];

    /*
     * "תקועה" = אף חודש שלה עוד לא עבר את האישור הראשוני, והיא כרגע ממתינה
     * לבדיקה או לחתימות - לא כל בקשה שנמצאת באיזשהו סטטוס ממתין (תמיכה חודשית
     * שרצה כרגיל, ורק החודש הבא שלה ממתין, אינה תקועה), ולא כל בקשה שעדיין לא
     * אושרה (isInInitialStatusProcess לבדו כולל גם בקשה שנדחתה או בוטלה).
     */
    if (request.isInInitialStatusProcess && statuses.some(isPendingInitialReview) && !isNaN(requestedAt) && requestedAt < staleThreshold) {
      staleCount++;
      if (oldestStale === null || requestedAt < oldestStale) oldestStale = requestedAt;
    }
  });

  const expiringSoon = supporteds.filter((supported) =>
    (supported.contactClassifications ?? []).some((classification) => getExpirationStatus(classification) === ExpirationStatus.ExpiringSoon)
  ).length;

  const items: AttentionItem[] = [];

  if (expiringSoon > 0) {
    items.push({
      id: 'expiringClassifications',
      severity: 'warning',
      title: `${expiringSoon} נתמכים שהסיווג שלהם יפוג בקרוב`,
      detail: `בתוך ${EXPIRATION_SOON_DAYS} הימים הקרובים`,
      count: expiringSoon,
      to: `/${fundId}/support/supporteds?expiration=${ExpirationStatus.ExpiringSoon}`,
    });
  }

  const pendingInitialApproval: PendingInitialApprovalInfo | null =
    staleCount > 0 ? { count: staleCount, oldestAt: oldestStale, to: requestsLink(fundId, range, STALE_REQUEST_DAYS) } : null;

  return { attention: items, pendingInitialApproval };
};

/* ============================ המודל ============================ */

export interface DashboardInput {
  transactions: Transaction[];
  /** בקשות התמיכה בתקופה הנבחרת. */
  requests: SupportRequestDtoIn[];
  /** כל הבקשות, לחלון של attentionRange. */
  attentionRequests: SupportRequestDtoIn[];
  standingOrders: NedarimStandingOrderDtoIn[];
  supporteds: SupportedDtoIn[];
  pendingDonations: PendingOfflineDonation[];
  balance: Balance | null | undefined;
  fundId: number;
  current: Range;
  previous: Range | null;
  attentionWindow: Range;
  flowGran: Granularity;
  now: number;
}

export const buildDashboardModel = ({
  transactions,
  requests,
  attentionRequests,
  standingOrders: orders,
  supporteds,
  pendingDonations: pendingDonationsIn,
  balance,
  fundId,
  current,
  previous,
  attentionWindow,
  flowGran,
  now,
}: DashboardInput) => {
  /* ---- מטבע הדיווח ----
   * הקרן מדווחת ב-ILS או ב-USD, ולכן גם סימן המטבע וגם ההמרה נגזרים מהיתרה
   * ולא מונחים כשקל. תנועה שאין לה המרה נספרת ומדווחת במקום להיכנס לסכום. */
  const fundCurrency = balance?.currency || 'ILS';
  const curSign = currencySymbol(fundCurrency);
  const all = transactions.filter((t) => reportingAmount(t, fundCurrency) !== null);

  /* היום של כל תנועה מחושב פעם אחת: בטווח רב-שנתי ההשוואה לדליים רצה מיליוני
   * פעמים, ופרסור מחרוזת התאריך בכל אחת מהן תקע את המסך לעשרות שניות. */
  const dayMs = new Map(transactions.map((t) => [t, txDate(t).getTime()]));
  const dayOf = (t: Transaction) => dayMs.get(t)!;

  const signed = (t: Transaction) => reportingAmount(t, fundCurrency) ?? 0;
  const txAmount = (t: Transaction) => Math.abs(signed(t));
  const isIncome = (t: Transaction) => signed(t) >= 0;

  const periodDays = daysInRange(current);

  const inRange = (t: Transaction, r: Range) => {
    const d = dayOf(t);
    return d >= r.from.getTime() && d <= r.to.getTime();
  };
  const tx = all.filter((t) => inRange(t, current));
  const txPrev = previous ? all.filter((t) => inRange(t, previous)) : [];

  // הכותרת מדברת על התקופה הנבחרת, ולכן נספרות רק תנועות שלא הומרו בתוכה —
  // ולא גם אלה שבחלון ההשוואה, שאינן מוצגות בשום מקום במסך.
  const unconvertedCount = transactions.filter((t) => reportingAmount(t, fundCurrency) === null && inRange(t, current)).length;

  /* סכום נטו: תרומה שהוחזרה (סכום שלילי) מקטינה את סך התרומות, ותמיכה שחזרה
   * (סכום חיובי) מקטינה את סך התמיכות. */
  const donationsNet = (list: Transaction[]) => list.filter(isDonation).reduce((s, t) => s + signed(t), 0);
  const payoutsNet = (list: Transaction[]) => list.filter(isPayout).reduce((s, t) => s - signed(t), 0);

  /* התשלומים עצמם, בלי ההחזרים — לכל מה שסופר תרומות או מציג אותן אחת-אחת. */
  const gifts = tx.filter((t) => isDonation(t) && signed(t) > 0);
  const paid = tx.filter((t) => isPayout(t) && signed(t) < 0);

  const sumAbs = (list: Transaction[]) => list.reduce((s, t) => s + txAmount(t), 0);

  /* מספר התרומות מול מספר החיובים.
   *
   * טבלת "פרטי תנועות" ב-APT היא טבלת התשלומים, והשרת מחזיר שורה לכל תשלום.
   * לכן תרומה אחת בתשלומים מחזירה כמה שורות: הסכומים נכונים לחיוב, אבל ספירת
   * שורות אינה ספירת תרומות. כל מקום שאומר "תרומות" סופר תנועות מובחנות. */
  const countDonations = (list: Transaction[]) => new Set(list.map((t) => t.transactionId)).size;

  /* מחלק רשימה לדליים במעבר אחד. הדליים רציפים ועולים, ולכן חיפוש בינארי מוצא
   * את הדלי של כל תנועה; סינון של כל הרשימה לכל דלי מחדש היה, בטווח רב-שנתי,
   * מאות אלפי מעברים לכל גרף. */
  const byBucket = (list: Transaction[], buckets: Bucket[]): Transaction[][] => {
    const out = buckets.map(() => [] as Transaction[]);
    const starts = buckets.map((b) => b.from.getTime());
    const ends = buckets.map((b) => b.to.getTime());
    list.forEach((t) => {
      const d = dayOf(t);
      let lo = 0;
      let hi = buckets.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (d < starts[mid]) hi = mid - 1;
        else if (d > ends[mid]) lo = mid + 1;
        else return void out[mid].push(t);
      }
    });
    return out;
  };

  /* ---- תזרים ----
   * cumulativeAmount הוא יתרת הקרן אחרי התנועה. השרת מחזיר את התנועות מהחדשה
   * לישנה ומחשב אותו בשרשרת מהשורה הראשונה, ולכן בתוך יום היתרה של סוף היום
   * יושבת על השורה שבאה ראשונה בסדר של השרת — לא על האחרונה.
   *
   * השרת ממלא את השדה רק לתנועות שאחרי מועד החישוב שלו, ובקרנות מסוימות לא בכלל.
   * לכן הקו הוא יתרת הקרן רק כשלכל התנועות בתקופה יש יתרה; אחרת הוא השינוי
   * המצטבר בתקופה, והכרטיס אומר זאת. ערבוב בין השניים היה מייצר קפיצה מדומה.
   */
  const flowGranularity = effectiveFlowGranularity(flowGran, periodDays);
  const flowBuckets = buildBuckets(current, flowGranularity);
  const serverOrder = new Map(transactions.map((t, index) => [t, index]));
  const inWindowRaw = transactions.filter((t) => inRange(t, current));
  const useCumulative = inWindowRaw.length > 0 && inWindowRaw.every((t) => (t.cumulativeAmount ?? 0) !== 0);

  let running = 0;
  let carry: number | null = null;
  const firstBalance = (() => {
    if (!useCumulative) return 0;
    // היתרה לפני התנועה הראשונה בתקופה = היתרה אחריה פחות הסכום שלה.
    const oldest = inWindowRaw.reduce((a, b) =>
      dayOf(b) < dayOf(a) || (dayOf(b) === dayOf(a) && serverOrder.get(b)! > serverOrder.get(a)!) ? b : a
    );
    return oldest.cumulativeAmount - (oldest.ilsAmount ?? oldest.amount);
  })();
  const txByFlow = byBucket(tx, flowBuckets);
  const rawByFlow = byBucket(inWindowRaw, flowBuckets);
  const flow: ChartRow[] = flowBuckets.map((b, bi) => {
    const inB = txByFlow[bi];
    const income = sumAbs(inB.filter(isIncome));
    const expense = sumAbs(inB.filter((t) => !isIncome(t)));
    running += income - expense;

    let balanceAt: number;
    if (useCumulative) {
      const rawInB = rawByFlow[bi];
      if (rawInB.length) {
        const closing = rawInB.reduce((a, c) =>
          dayOf(c) > dayOf(a) || (dayOf(c) === dayOf(a) && serverOrder.get(c)! < serverOrder.get(a)!) ? c : a
        );
        carry = closing.cumulativeAmount;
      }
      balanceAt = carry ?? firstBalance;
    } else {
      balanceAt = running;
    }
    return { label: b.label, full: b.full, income, expense, balance: balanceAt };
  });

  /* ---- אמצעי תשלום ----
   * נטו לכל אמצעי: החזר בכרטיס אשראי יורד מהאשראי. אמצעי שיצא אפס או פחות לא מוצג. */
  const byMethod = new Map<string, number>();
  tx.filter(isDonation).forEach((t) => {
    const key = t.paymentMethod || 'other';
    byMethod.set(key, (byMethod.get(key) ?? 0) + signed(t));
  });
  const channels: PaymentSlice[] = [...byMethod.entries()]
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value], index) => ({ key, value, color: PAYMENT_COLORS[index % PAYMENT_COLORS.length] }));

  /* ---- תרומות לפי יום ---- */
  // בשנה שלמה עמודה ליום היא בלתי קריאה, ולכן הדליים הופכים לחודשים — ואז גם
  // הממוצע הנע הוא של 7 חודשים ולא 7 ימים. מעבר לטווח של יותר משנה וחצי
  // בערך (משימה 1144), עוברים לעמודה לשנה, בלי ממוצע נע.
  const donationBucketGran: Granularity = periodDays > YEAR_VIEW_THRESHOLD_DAYS ? 'year' : periodDays > MONTH_VIEW_THRESHOLD_DAYS ? 'month' : 'day';
  const dayBuckets = buildBuckets(current, donationBucketGran);
  const txByDay = byBucket(tx, dayBuckets);
  const giftsByDay = byBucket(gifts, dayBuckets);
  const perDay = dayBuckets.map((b, bi) => ({ bucket: b, sum: donationsNet(txByDay[bi]), count: countDonations(giftsByDay[bi]) }));
  const movingAvg = (vals: number[]) =>
    vals.map((_, i) => {
      const slice = vals.slice(Math.max(0, i - 6), i + 1);
      return slice.reduce((a, c) => a + c, 0) / slice.length;
    });
  const maSum = movingAvg(perDay.map((p) => p.sum));
  const maCount = movingAvg(perDay.map((p) => p.count));
  const donationsBySum: ChartRow[] = perDay.map((p, i) => ({ label: p.bucket.label, full: p.bucket.full, v: Math.max(0, p.sum), ma: maSum[i] }));
  const donationsByCount: ChartRow[] = perDay.map((p, i) => ({ label: p.bucket.label, full: p.bucket.full, v: p.count, ma: maCount[i] }));

  /* ---- מפת חום: שבוע בחלון × יום בשבוע ---- */
  const weekCount = Math.max(1, Math.ceil(periodDays / 7));
  const heatRows = Array.from({ length: weekCount }, (_, i) => {
    const from = addDays(current.from, i * 7);
    const to = addDays(from, 6) > current.to ? current.to : addDays(from, 6);
    return `${dm(from)}–${dm(to)}`;
  });
  // תא = תרומות מובחנות, לא חיובים: תרומה בתשלומים לא נספרת פעמיים באותו תא.
  const heatSeen = heatRows.map(() => HE_DAYS.map(() => new Set<number>()));
  gifts.forEach((t) => {
    const d = new Date(dayOf(t));
    const dayOffset = Math.round((d.getTime() - current.from.getTime()) / DAY_MS);
    const wi = Math.min(weekCount - 1, Math.max(0, Math.floor(dayOffset / 7)));
    heatSeen[wi][d.getDay()].add(t.transactionId);
  });
  const heatValues = heatSeen.map((row) => row.map((cell) => cell.size));

  /* ---- התפלגות גובה תרומה ---- */
  const amounts = gifts.map(txAmount);
  const histBuckets = amountBuckets(curSign);
  const amountHist: ChartRow[] = histBuckets.map((b, i) => {
    const min = i === 0 ? 0 : histBuckets[i - 1].max;
    return { label: b.label, full: b.label, v: amounts.filter((a) => a > min && a <= b.max).length };
  });

  /* ---- תורמים מובילים ----
   * הקיבוץ לפי מזהה איש הקשר ולא לפי השם: שני תורמים שונים בעלי אותו שם היו
   * מתמזגים לשורה אחת. הסכום נטו, ותורם שכל תרומתו הוחזרה לא מופיע. */
  const donorKey = (t: Transaction) => (t.contactId ? `id:${t.contactId}` : `name:${t.contactName?.trim() || 'ללא שם'}`);
  const byDonor = new Map<string, { name: string; total: number; movements: Set<number> }>();
  tx.filter(isDonation).forEach((t) => {
    const key = donorKey(t);
    const prev = byDonor.get(key) ?? { name: t.contactName?.trim() || 'ללא שם', total: 0, movements: new Set<number>() };
    if (signed(t) > 0) prev.movements.add(t.transactionId);
    prev.total += signed(t);
    byDonor.set(key, prev);
  });
  const topDonors: RankItem[] = [...byDonor.values()]
    .filter((donor) => donor.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)
    .map((donor) => ({ name: donor.name, value: donor.total, sub: `${donor.movements.size} תרומות`, color: CHART.wine }));

  /* ---- חדשים מול חוזרים (בתוך החלון שנטען) ----
   * "חדש" = התרומה הראשונה של אותו תורם בתוך החלון שנטען. נספרות תרומות ולא
   * חיובים: תרומה בתשלומים היא תרומה אחת, ונספרת בדלי שבו נרשם החיוב הראשון שלה. */
  const firstChargeOf = new Map<number, Transaction>();
  [...all]
    .filter((t) => isDonation(t) && signed(t) > 0)
    .sort((a, b) => dayOf(a) - dayOf(b))
    .forEach((t) => {
      if (!firstChargeOf.has(t.transactionId)) firstChargeOf.set(t.transactionId, t);
    });
  const seenDonors = new Set<string>();
  const isNewDonation = new Map<number, boolean>();
  [...firstChargeOf.values()].forEach((t) => {
    const key = donorKey(t);
    isNewDonation.set(t.transactionId, !seenDonors.has(key));
    seenDonors.add(key);
  });
  const nvrBuckets = buildBuckets(current, periodDays > MONTH_VIEW_THRESHOLD_DAYS ? 'month' : 'week');
  const firstByNvr = byBucket([...firstChargeOf.values()], nvrBuckets);
  const newVsReturning: ChartRow[] = nvrBuckets.map((b, bi) => {
    const nw = firstByNvr[bi].filter((t) => isNewDonation.get(t.transactionId)).length;
    return { label: b.label, full: b.full, nw, ret: firstByNvr[bi].length - nw };
  });

  /* ---- תמיכות לפי יעד ---- */
  const byGoal = new Map<string, { approved: number; people: Set<number | string> }>();
  requests.forEach((r) => {
    const approved = approvedInRange(r, current);
    if (approved <= 0) return;
    const name = r.goal?.name ?? 'ללא יעד';
    const cur = byGoal.get(name) ?? { approved: 0, people: new Set<number | string>() };
    cur.approved += approved;
    // לפי מזהה הנתמך, כדי ששני נתמכים בעלי אותו שם לא יתמזגו לאחד.
    if (r.supportedContact) cur.people.add(r.supportedContact.id ?? `${r.supportedContact.firstName} ${r.supportedContact.lastName}`);
    byGoal.set(name, cur);
  });
  const supportsByGoal = [...byGoal.entries()]
    .map(([name, v]) => ({ name, amount: v.approved, families: v.people.size }))
    .sort((a, b) => b.amount - a.amount);

  /* ---- הוראות קבע ----
   * "פעילה" נקבע באותו כלל שבמסך הוראות הקבע. כל הוראה במטבע שלה: הגבייה הצפויה
   * מסוכמת לכל מטבע בנפרד, כמו שם, כי שקלים ודולרים אינם מתחברים למספר אחד.
   * ההתפלגות לפי סכום חיוב היא של ההוראות במטבע הקרן בלבד, כי הטווחים בשקלים. */
  const activeOrders = orders.filter(isCollecting);
  const orderBuckets = soBuckets(curSign);
  const activeInFundCurrency = activeOrders.filter((o) => getCurrencyIso(o) === fundCurrency);
  const expectedByCurrency = sumByCurrency(activeOrders, (o) => o.amount ?? 0);
  const standingOrders = orders.length
    ? {
        active: activeOrders.length,
        // סכום החיובים של ההוראות הפעילות — אותו מספר שמסך הוראות הקבע קורא לו
        // "גבייה צפויה". אין בנתון שדה תדירות, ולכן אין כאן טענה שזה חודשי.
        // מטבע הקרן ראשון, אחריו השאר.
        expected: Object.entries(expectedByCurrency)
          .sort(([a], [b]) => (a === fundCurrency ? -1 : b === fundCurrency ? 1 : a.localeCompare(b)))
          .map(([currency, amount]) => ({ currency, amount })),
        total: orders.length,
        otherCurrencyActive: activeOrders.length - activeInFundCurrency.length,
        byAmount: orderBuckets.map((b, i) => {
          const min = i === 0 ? 0 : orderBuckets[i - 1].max;
          return {
            label: b.label,
            full: b.label,
            v: activeInFundCurrency.filter((o) => (o.amount ?? 0) > min && (o.amount ?? 0) <= b.max).length,
          };
        }) as ChartRow[],
      }
    : null;

  /* ---- ספארקליינים ל-KPI ---- */
  const sparkBuckets = buildBuckets(current, periodDays > MONTH_VIEW_THRESHOLD_DAYS ? 'month' : 'day');
  const txBySpark = byBucket(tx, sparkBuckets);
  const sparkOf = (net: (list: Transaction[]) => number) => txBySpark.map(net);

  // מספר הנתמכים המובחנים — לפי מזהה איש הקשר, כי שמות זהים היו מתמזגים לאחד.
  const supportFamilies = new Set(paid.map(donorKey)).size;

  /* ---- יתרה, והתחייבויות שיושבות עליה ----
   * ארבעת המנוכים מוחזרים בנפרד כי הכרטיס חייב להסביר את הפער בין "סה״כ בקרן"
   * ל"יתרה זמינה".
   *
   * היתרה הזמינה מחושבת כאן ולא דרך Balance.calculateAvailableBalance בכוונה:
   * המתודה המשותפת אינה מנכה את הקפאת מכשירי נדרים, בשונה מהשרת
   * (FundBalances.CalculateAvailableBalance). כאן הנוסחה זהה לשרת. ראו את ההערה
   * על המתודה ב-Balance.ts.
   *
   * המסגרת נשמרת בסימן: מסגרת אשראי (מינוס) היא שלילית ולכן מוסיפה ליתרה. */
  const totalBalance = balance?.balancesByKind?.total ?? null;
  const pendingExecution = balance?.supportRequestsBalances?.pendingExecutionSum ?? 0;
  const frozenRequests = balance?.supportRequestsBalances?.frozenRequestsSum ?? 0;
  const overdraft = balance?.overdraftAmount ?? 0;
  const devicesFreeze = balance?.nedarimDevicesFreezeAmount ?? 0;
  const commitments = pendingExecution + frozenRequests + overdraft + devicesFreeze;
  const availableBalance = totalBalance === null ? null : totalBalance - commitments;

  /* ---- תרומות שממתינות לאישור ----
   * מוצגות רק לקרן שדגל התרומות דלוק אצלה. אושרו ונדחו יוצאות מהרשימה, וגם צ׳ק
   * דחוי שמועד הפירעון שלו עוד לא הגיע (FutureCheck): אין מה לאשר בו עדיין, והשרת
   * מעביר אותו לממתין ביום שמועדו מגיע. */
  const pendingDonations = pendingDonationsIn
    .filter((donation) => donation.status === 'Pending' || donation.status === 'InProgress')
    .map((donation) => ({
      id: donation.id,
      donor: donation.donorFullName,
      type: donation.type,
      amount: donation.amount,
      currency: donation.currency || 'ILS',
      // תאריך שלא ניתן לפרסר מחזיר null ולא NaN, כדי שהטבלה תציג מקף ולא "NaN ימים".
      ageDays: (() => {
        const created = new Date(donation.createdAt).getTime();
        return isNaN(created) ? null : Math.max(0, Math.floor((now - created) / DAY_MS));
      })(),
    }))
    .sort((a, b) => (b.ageDays ?? -1) - (a.ageDays ?? -1));

  const { attention, pendingInitialApproval } = buildAttention(attentionRequests, supporteds, fundId, attentionWindow, now);

  return {
    fundCurrency,
    unconvertedCount,
    attention,
    pendingInitialApproval,
    pendingDonations,
    kpi: {
      availableBalance,
      totalBalance,
      pendingExecution,
      frozenRequests,
      overdraft,
      devicesFreeze,
      commitments,
      attentionCount: attention.reduce((s, item) => s + item.count, 0),
      donations: donationsNet(tx),
      /** null = אין תקופה להשוות אליה. */
      donationsPrev: previous ? donationsNet(txPrev) : null,
      donationsCount: countDonations(gifts),
      supports: payoutsNet(tx),
      supportsPrev: previous ? payoutsNet(txPrev) : null,
      supportFamilies,
      donationSpark: sparkOf(donationsNet),
      supportSpark: sparkOf(payoutsNet),
    },
    periodDays,
    flow,
    flowGranularity,
    /* כשאין נתוני יתרה מצטברת מהשרת, הקו הוא הסכימה המקומית בתקופה — כלומר
       השינוי המצטבר ולא יתרת הקרן. הכרטיס אומר מה מוצג בפועל. */
    flowLineIsFundBalance: useCumulative,
    channels,
    donationsBySum,
    donationsByCount,
    heat: { rows: heatRows, cols: HE_DAYS, values: heatValues },
    amountHist,
    amountStats: {
      avg: amounts.length ? sumAbs(gifts) / amounts.length : 0,
      median: median(amounts),
      max: amounts.length ? Math.max(...amounts) : 0,
    },
    topDonors,
    newVsReturning,
    // השורות מוכנות כאן ולא בתצוגה, כדי שהסכום המוצג יעבור באותה המרה כמו הסכומים בגרפים.
    recent: [...gifts]
      .sort((a, b) => dayOf(b) - dayOf(a))
      .slice(0, 6)
      .map((t) => ({
        key: `${t.transactionId}-${t.transactionDetailId}`,
        date: new Date(dayOf(t)),
        // השרת מרכיב את השם מ"משפחה פרטי"; בתנועה בלי איש קשר הוא רווח בודד.
        donor: t.contactName?.trim() || 'ללא שם',
        paymentMethod: t.paymentMethod,
        amount: txAmount(t),
      })),
    /* עשר התמיכות והמלגות האחרונות שיצאו בפועל.
     * מהתנועות ולא מהבקשות, בכוונה: בקשה מאושרת אינה כסף ששולם. */
    recentSupports: [...paid]
      .sort((a, b) => dayOf(b) - dayOf(a))
      .slice(0, 10)
      .map((t) => ({
        key: `${t.transactionId}-${t.transactionDetailId}`,
        date: new Date(dayOf(t)),
        name: t.contactName?.trim() || 'ללא שם',
        kind: t.kind,
        amount: txAmount(t),
      })),
    supportsByGoal,
    standingOrders,
    donationBucketGran,
    counts: { transactions: tx.length },
  };
};

export type DashboardModel = ReturnType<typeof buildDashboardModel>;

/* ============================ ההוק ============================ */

/** התקופה שהמסך נפתח עליה. ההוק והטעינה המוקדמת (useDashboardPrefetch) חייבים להסכים עליה. */
export const DEFAULT_DASHBOARD_PERIOD: PeriodKey = 'month';

/**
 * SPR-10303: התנועות והיתרות בפתיחה ובסגירה נשמרות דקה לפני שנשאלות שוב.
 *
 * הן נטענות מראש, עוד לפני שהמסך עולה (ר' useDashboardPrefetch). ב-staleTime: 0
 * הרגיל, טעינה מוקדמת שהספיקה לחזור הייתה נשלחת שוב ברגע שהמסך עלה - פעמיים
 * אותה קריאה כבדה. "רענון" באזור האישי מרענן אותן גם בתוך הדקה.
 */
export const DASHBOARD_CORE_STALE_MS = 60 * 1000;

/**
 * שתי הכתובות של המסך שאינן תלויות בדגלים או בעולמות ההרשאה: התנועות (חלון כפול,
 * התקופה הנוכחית + הקודמת, לחישוב מגמה בקריאה אחת) והיתרה בפתיחת התקופה ובסגירתה
 * (משימה 1448). השרת מסנן את שתיהן לעולמות של הרכז, ולכן מותר לשאול אותן לפני
 * שנודע מה הרכז רואה.
 */
const coreUrls = (fundId: number | undefined, current: Range, previous: Range | null) => ({
  txUrl: fundId ? `funds/financial-data/${fundId}/transactions?start=${iso((previous ?? current).from)}&end=${iso(current.to)}` : '',
  balanceRangeUrl: fundId ? `funds/financial-data/${fundId}/balance-range?start=${iso(current.from)}&end=${iso(current.to)}` : '',
});

/** אותן כתובות לפי תקופה - לטעינה המוקדמת, שאין לה טווחים מחושבים. */
export const dashboardCoreUrls = (fundId: number, period: PeriodKey = DEFAULT_DASHBOARD_PERIOD) => {
  const { current, previous } = resolveRanges(period);
  return coreUrls(fundId, current, previous);
};

export const useDashboardData = (period: PeriodKey, flowGran: Granularity, customRange?: CustomRange) => {
  const { currentFund, balance } = useFundContext();
  const fundId = currentFund?.id;
  const donationsEnabled = useFeature('donations');
  /* עולמות ההרשאה (דרוק, 22.10.2026): עולם שסגור בפני הרכז לא נשאל בכלל - השרת
     היה דוחה את הקריאה - והכרטיסים שלו לא מוצגים. התנועות עצמן מגיעות מהשרת
     מסוננות לעולמות שהרכז רואה. */
  const { canView } = useFundAccess(fundId);
  const seesDonations = canView('Donations');
  const seesSupports = canView('Supports');
  const standingOrdersEnabled = useFeature('standing-orders');
  const { current, previous } = useMemo(() => resolveRanges(period, customRange), [period, customRange]);
  const attentionWindow = useMemo(attentionRange, []);

  const { txUrl, balanceRangeUrl } = coreUrls(fundId, current, previous);
  const reqUrl = fundId && seesSupports ? `funds/${fundId}/support-requests?dateFrom=${iso(current.from)}&dateTo=${iso(current.to)}` : '';
  /* קריאה נפרדת ל"ממתינות לאישור ראשוני", על כל הבקשות. הכתובת קבועה לכל קרן,
     ולכן react-query מגיש אותה מהמטמון כשמחליפים תקופה. */
  const attentionReqUrl =
    fundId && seesSupports ? `funds/${fundId}/support-requests?dateFrom=${iso(attentionWindow.from)}&dateTo=${iso(attentionWindow.to)}` : '';
  // שתי הקריאות האלה חסומות בשרת ב-RequireFeature, ולכן הן נשלחות רק לקרן שהדגל דלוק אצלה.
  const soUrl = fundId && standingOrdersEnabled && seesDonations ? `nedarim-standing-orders/${fundId}` : '';
  const supportedsUrl = fundId && seesSupports ? `funds/${fundId}/supporteds` : '';
  const pendingUrl = fundId && donationsEnabled && seesDonations ? `donations/pending/${fundId}` : '';

  /* חלק מ"הנתונים של המסך" לעניין רענון האזור האישי (ר' useGlobalRefresh). */
  const refreshable = { meta: { refreshable: true } satisfies RefreshableQueryMeta };
  /* התנועות והיתרות נטענות מראש (ר' DASHBOARD_CORE_STALE_MS), ולכן לא נשאלות שוב מיד. */
  const core = { ...refreshable, staleTime: DASHBOARD_CORE_STALE_MS };

  const txQuery = useGetAuthApi<Transaction[]>(txUrl, undefined, core);
  const reqQuery = useGetAuthApi<SupportRequestDtoIn[]>(reqUrl, undefined, refreshable);
  const attentionReqQuery = useGetAuthApi<SupportRequestDtoIn[]>(attentionReqUrl, undefined, refreshable);
  /*
   * ⚠️ בכוונה בלי meta.refreshable ובלי ניסיון חוזר: זו אותה קריאה ל-GetKevaJson של
   * נדרים שהלוח של הוראות הקבע קורא לה, והיא מוגבלת לעשרים קריאות בשעה למוסד -
   * מכסה משותפת לכל הקרנות שתחתיו. ה-retry: 2 הכללי היה הופך כישלון אחד לשלוש
   * קריאות, ו-staleTime: 0 היה קורא לה בכל כניסה למסך; חמש דקות הן גם זמן
   * המטמון שלה בשרת.
   */
  const soQuery = useGetAuthApi<NedarimStandingOrderDtoIn[]>(soUrl, undefined, { retry: false, staleTime: 5 * 60 * 1000 });
  const supportedsQuery = useGetAuthApi<SupportedDtoIn[]>(supportedsUrl, undefined, refreshable);
  const pendingQuery = useGetAuthApi<PendingOfflineDonation[]>(pendingUrl, undefined, refreshable);
  /* משימה 1448: היתרה בפתיחת התקופה ובסגירתה, כמו בתדפיס בנק. השרת מחשב את שתיהן
     מכל התנועות של הקרן, עמלות כלולות, ולכן הן מתיישבות גם כשהגרף לא. */
  const balanceRangeQuery = useGetAuthApi<BalanceRange>(balanceRangeUrl, undefined, core);

  const model = useMemo(
    () =>
      buildDashboardModel({
        transactions: txQuery.data ?? [],
        requests: reqQuery.data ?? [],
        attentionRequests: attentionReqQuery.data ?? [],
        standingOrders: soQuery.data ?? [],
        supporteds: supportedsQuery.data ?? [],
        pendingDonations: pendingQuery.data ?? [],
        balance,
        fundId: fundId ?? 0,
        current,
        previous,
        attentionWindow,
        flowGran,
        now: Date.now(),
      }),
    [txQuery.data, reqQuery.data, attentionReqQuery.data, soQuery.data, supportedsQuery.data, pendingQuery.data, balance, fundId, current, previous, attentionWindow, flowGran]
  );

  const rangeLabel = `${iso(current.from).split('-').reverse().join('.')} – ${iso(current.to).split('-').reverse().join('.')}`;

  return {
    model,
    rangeLabel,
    compareLabel: PERIOD_COMPARE[period],
    isLoading: txQuery.isLoading || reqQuery.isLoading,
    isError: txQuery.isError,
    /** הבקשות של התקופה נכשלו - "יעדי תמיכה מובילים" אומר את זה ולא מציג ריק. */
    isRequestsError: reqQuery.isError,
    hasFund: !!fundId,
    /** הנתמכים והתרומות הממתינות נטענים אחרי הגרפים, ולכן דגל טעינה נפרד לכרטיסים שלהם. */
    isAttentionLoading: attentionReqQuery.isLoading || supportedsQuery.isLoading,
    isPendingDonationsEnabled: donationsEnabled && seesDonations,
    seesDonations,
    seesSupports,
    isPendingDonationsLoading: pendingQuery.isLoading,
    /** undefined בזמן טעינה, כשהקריאה נכשלה או כשחזר משהו שאינו יתרה - השורה פשוט לא מוצגת. */
    balanceRange: typeof balanceRangeQuery.data?.openingBalance === 'number' ? balanceRangeQuery.data : undefined,
  };
};
